/**
 * Timeline Component
 *
 * A clean, organized timeline visualization displaying all events related to an entity (Task, Client, or Resource).
 * Events are grouped by date in reverse chronological order (newest first).
 *
 * Features:
 * - 📅 Clean date-based organization with subtle separators
 * - 🔍 Filterable event types (status, schedule, planning, reminders, history) - shows all by default
 * - 🎯 Consistent card layout with color-coded icons
 * - 👥 Visual representation of assigned resources and notified users
 * - ✨ Interactive event cards with modal details
 * - 🌐 Entity-aware: adapts to Task, Client, or Resource contexts
 *
 * Filter Behavior:
 * - Default: All event types shown (no active filters)
 * - Active filters: Only selected types displayed
 * - Deselecting all filters returns to showing everything
 *
 * Entity-Specific Behavior:
 * - Task: Shows status, schedule, planning, and reminders
 * - Client/Resource: Shows history (task events) and reminders
 */

import React from 'react';
import type { EntityWidgetProps } from '@/other/constants/EntityManagerConfig';
import type { TimelineMode } from './utils/types';
import { TimelineProvider, useTimelineContext } from './context/TimelineContext';
import { TimelineHeader } from './components/TimelineHeader';
import { TimelineEventList } from './components/TimelineEventList';
import { TimelineFooter } from './components/TimelineFooter';
import ReminderDetailModal from './editors/ReminderDetailModal';
import StatusChangeModal from './editors/StatusChangeModal';
import PlanningDetailModal from './editors/PlanningDetailModal';
import { ResourceDetailModal } from './editors/ResourceDetailModal';
import HistoryDetailModal from './editors/HistoryDetailModal';
import ReminderEditModal from './editors/ReminderEditModal';
import ScheduleEditModal from './editors/ScheduleEditModal';
import PlanningEditModal from './editors/PlanningEditModal';

const TimelineContent: React.FC = () => {
  const {
    record,
    entityType,
    reminderModal,
    statusModal,
    planningModal,
    resourceModal,
    historyModal,
    closeReminderModal,
    closeStatusModal,
    closePlanningModal,
    closeResourceModal,
    closeHistoryModal,
    reminderEditModal,
    setReminderEditModal,
    scheduleEditModal,
    setScheduleEditModal,
    planningEditModal,
    setPlanningEditModal,
  } = useTimelineContext();

  return (
    <>
      <div className="h-full rounded-t-md overflow-hidden border flex flex-col min-h-0 flex-1">
        <TimelineHeader />
        <TimelineEventList />
        <TimelineFooter />
      </div>

      {/* Detail Modals */}
      {resourceModal.data && (
        <ResourceDetailModal
          open={resourceModal.isOpen}
          onOpenChange={closeResourceModal}
          resources={resourceModal.data}
        />
      )}
      {planningModal.data && (
        <PlanningDetailModal
          open={planningModal.isOpen}
          onOpenChange={closePlanningModal}
          planningEvents={planningModal.data}
        />
      )}
      {statusModal.data && (
        <StatusChangeModal
          open={statusModal.isOpen}
          onOpenChange={closeStatusModal}
          statusChanges={statusModal.data}
        />
      )}
      {reminderModal.data && (
        <ReminderDetailModal
          open={reminderModal.isOpen}
          onOpenChange={closeReminderModal}
          reminder={reminderModal.data}
          notifyUsers={reminderModal.data.reminderRecipientValues || []}
        />
      )}
      {historyModal.data && (
        <HistoryDetailModal
          open={historyModal.isOpen}
          onOpenChange={closeHistoryModal}
          historyEvent={historyModal.data}
        />
      )}

      {/* Edit Modals */}
      <ReminderEditModal
        open={reminderEditModal.isOpen}
        onOpenChange={(isOpen) => setReminderEditModal({ ...reminderEditModal, isOpen })}
        mode={reminderEditModal.mode}
        reminderId={reminderEditModal.reminderId}
        entityId={record?.id as string}
        entityType={entityType}
        defaultFormValues={reminderEditModal.defaultFormValues}
      />
      <ScheduleEditModal
        open={scheduleEditModal.isOpen}
        onOpenChange={(isOpen) => setScheduleEditModal({ ...scheduleEditModal, isOpen })}
        mode={scheduleEditModal.mode}
        eventId={scheduleEditModal.eventId}
        taskId={record?.id as string}
      />
      <PlanningEditModal
        open={planningEditModal.isOpen}
        onOpenChange={(isOpen) => setPlanningEditModal({ ...planningEditModal, isOpen })}
        mode={planningEditModal.mode}
        planningId={planningEditModal.planningId}
        taskId={record?.id as string}
        defaultFormValues={planningEditModal.defaultFormValues}
      />
    </>
  );
};

export interface TimelineWidgetProps extends EntityWidgetProps {
  initialMode?: TimelineMode;
}

export const Timeline = ({ record, entityType, onClose, headerTitle, initialMode }: TimelineWidgetProps) => {
  return (
    <TimelineProvider record={record} entityType={entityType} onClose={onClose} headerTitle={headerTitle} initialMode={initialMode}>
      <TimelineContent />
    </TimelineProvider>
  );
};
